'use client';
import { useMemo } from 'react';
import { Drawer, Group, Text, Box, Stack, Badge, Button, Tooltip } from '@mantine/core';
import { IconClipboardList, IconCheck, IconDownload } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { getThumbUrl } from '@/lib/imageUrl';
import { downloadBsx, buildBsx, bagRemarksLabel } from '@/lib/bsx';
import { trackEvent } from '@/lib/telemetry';

// Missing-parts-rapport in Sort mode: alles wat (nog) niet is afgevinkt,
// per zak, met export naar BSX/CSV/tekst. Eén-op-één geport uit de oude
// check-mode-modal op de overzichtspagina.
export function MissingPartsSheet({ opened, onClose, setId, set, checkedParts, totalProgress }) {
  const missingParts = useMemo(() => {
    if (!opened || !set?.bags) return [];
    const rows = [];
    set.bags.forEach(bag => {
      if (bag.bagNumber === '0') return;
      (bag.parts || []).forEach(part => {
        const key = `${bag.id}_${part.partId}_${part.color_id || 'nocolor'}`;
        const v = checkedParts[key];
        const count = typeof v === 'number' ? v : (v ? (part.quantity || 0) : 0);
        const missing = (part.quantity || 0) - Math.min(count, part.quantity || 0);
        if (missing > 0) rows.push({ ...part, bagLabel: bagRemarksLabel(bag), missing });
      });
    });
    return rows;
  }, [opened, set, checkedParts]);

  const missingWithoutBl = missingParts.filter(p => !p.bl_part_id || p.bl_color_id === null || p.bl_color_id === undefined).length;
  const bagCount = new Set(missingParts.map(p => p.bagLabel)).size;

  const handleExportBsx = () => {
    // Zelfde opzet als downloadSetBsx: één regel per part per zak, met de
    // zak in Remarks - alleen met de missende aantallen als Qty
    const items = [];
    const skipped = [];
    missingParts.forEach(p => {
      if (!p.bl_part_id || p.bl_color_id === null || p.bl_color_id === undefined) {
        skipped.push(p.partId);
        return;
      }
      items.push({
        blPartId: p.bl_part_id,
        blColorId: Number(p.bl_color_id),
        qty: p.missing,
        remarks: `LEGO ${setId} - ${p.bagLabel}`,
      });
    });
    downloadBsx(`lego-${setId}-missing.bsx`, buildBsx(items, skipped));
    trackEvent('Check Report Exported', { set: setId, format: 'bsx' });
  };

  const handleExportCsv = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = ['missing,element_id,name,color,bag,bricklink_part,bricklink_color'];
    missingParts.forEach(p => {
      lines.push([p.missing, esc(p.partId), esc(p.name), esc(p.colorName), esc(p.bagLabel), esc(p.bl_part_id || ''), p.bl_color_id ?? ''].join(','));
    });
    downloadBsx(`lego-${setId}-missing.csv`, lines.join('\n'), 'text/csv');
    trackEvent('Check Report Exported', { set: setId, format: 'csv' });
  };

  const handleCopyReport = async () => {
    const text = missingParts
      .map(p => `${p.missing}x ${p.name} (${p.partId}${p.colorName ? `, ${p.colorName}` : ''}) · ${p.bagLabel}`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      notifications.show({ message: 'Missing parts copied to clipboard', color: 'green', autoClose: 2500 });
      trackEvent('Check Report Exported', { set: setId, format: 'text' });
    } catch (e) {
      notifications.show({ message: 'Could not copy to clipboard', color: 'red', autoClose: 2500 });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      title={<Group gap="xs"><IconClipboardList size={18} /><Text fw={700}>Missing parts</Text></Group>}
      radius="lg"
      size="auto"
      zIndex={400}
      styles={{
        inner: { alignItems: 'flex-end' },
        content: { height: 'auto', minHeight: 0, maxHeight: '85dvh', display: 'flex', flexDirection: 'column' },
        body: { overflowY: 'auto', minHeight: 0 },
      }}
    >
      {missingParts.length === 0 ? (
        <Stack align="center" py="lg" gap="xs">
          <IconCheck size={36} color="#2f9e44" />
          <Text fw={600} c="#2f9e44">Nothing missing. Every piece is checked off!</Text>
        </Stack>
      ) : (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {totalProgress.checked} of {totalProgress.total} pieces found.{' '}
            <Text component="span" fw={700} c="red">{totalProgress.total - totalProgress.checked} missing</Text>{' '}
            across {bagCount} bag{bagCount === 1 ? '' : 's'}.
            Pieces you haven&apos;t checked off yet count as missing.
          </Text>
          <Box>
            {missingParts.map((p, idx) => (
              <Group key={`${p.bagLabel}-${p.partId}-${p.color_id || 'nocolor'}-${idx}`} gap="sm" wrap="nowrap" py={6} style={{ borderBottom: '1px solid #f1f3f5' }}>
                <Text fw={800} c="red" size="sm" style={{ width: 38, flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.missing}×</Text>
                <img
                  src={getThumbUrl(p.imageUrl, 96)}
                  alt={p.name}
                  style={{ width: 40, height: 40, objectFit: 'contain', mixBlendMode: 'multiply', flexShrink: 0 }}
                />
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={600} lineClamp={1}>{p.name}</Text>
                  <Text size="xs" c="dimmed">{p.partId}{p.colorName ? ` · ${p.colorName}` : ''}</Text>
                </Box>
                <Badge variant="light" color="gray" radius="sm" style={{ flexShrink: 0 }}>{p.bagLabel}</Badge>
              </Group>
            ))}
          </Box>
          {missingWithoutBl > 0 && (
            <Text size="xs" c="dimmed">
              {missingWithoutBl} part{missingWithoutBl === 1 ? ' has' : 's have'} no BrickLink mapping yet and will be left out of the BSX file.
            </Text>
          )}
          <Group gap="xs" wrap="wrap" pb="xs">
            <Tooltip label="Inventory file for BrickStore/BrickStock, with bag numbers as remarks" withArrow>
              <Button size="xs" leftSection={<IconDownload size={14} />} onClick={handleExportBsx} disabled={missingParts.length === missingWithoutBl}>
                Download BSX
              </Button>
            </Tooltip>
            <Button size="xs" variant="light" onClick={handleCopyReport}>Copy as text</Button>
            <Button size="xs" variant="light" onClick={handleExportCsv}>CSV</Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}
